// A site's map: its zones drawn over the map picture (or a plain grid until the site
// has one), in the same layout the tablet and phones use.

import type { Zone } from '../../../spec/src/formats.ts';

export type BBox = [number, number, number, number];

/** Width 1000; the height keeps the real proportions of the area. */
export function mapSize(bbox: BBox): { w: number; h: number } {
  const [w, s, e, n] = bbox;
  const midLat = ((s + n) / 2) * (Math.PI / 180);
  const widthKm = (e - w) * 111.32 * Math.cos(midLat);
  const heightKm = (n - s) * 110.574;
  return { w: 1000, h: Math.round((1000 * heightKm) / widthKm) };
}

export function project(bbox: BBox, lon: number, lat: number): [number, number] {
  const { w, h } = mapSize(bbox);
  return [((lon - bbox[0]) / (bbox[2] - bbox[0])) * w, ((bbox[3] - lat) / (bbox[3] - bbox[1])) * h];
}

/** The smallest map area that holds every zone, with a margin. */
export function bboxAround(zones: Zone[], marginShare = 0.08): BBox | null {
  const pts = zones.flatMap((z) => z.polygon);
  if (pts.length === 0) return null;
  let [w, s, e, n] = [Infinity, Infinity, -Infinity, -Infinity];
  for (const [lon, lat] of pts) {
    w = Math.min(w, lon);
    e = Math.max(e, lon);
    s = Math.min(s, lat);
    n = Math.max(n, lat);
  }
  const mx = (e - w) * marginShare || 0.002;
  const my = (n - s) * marginShare || 0.002;
  return [w - mx, s - my, e + mx, n + my];
}

export function SiteMap({ bbox, zones, image, highlight, alt }: { bbox: BBox; zones: Zone[]; image?: string | null; highlight?: string | null; alt: string }) {
  const { w, h } = mapSize(bbox);
  const grid: number[] = [];
  for (let i = 1; i < 10; i++) grid.push(i);
  return (
    <div className="map">
      <svg viewBox={`0 0 ${w} ${h}`} role="img" aria-label={alt}>
        <rect x="0" y="0" width={w} height={h} fill="#0A0C0F" />
        {image ? (
          <image href={image} x="0" y="0" width={w} height={h} preserveAspectRatio="none" style={{ filter: 'saturate(0.55) brightness(0.42) contrast(1.3)' }} />
        ) : (
          <g stroke="#15191E" strokeWidth="1">
            {grid.map((i) => (
              <line key={`v${i}`} x1={(w * i) / 10} y1="0" x2={(w * i) / 10} y2={h} />
            ))}
            {grid.map((i) => (
              <line key={`h${i}`} x1="0" y1={(h * i) / 10} x2={w} y2={(h * i) / 10} />
            ))}
          </g>
        )}
        {zones.map((z) => {
          const pts = z.polygon.map(([lon, lat]) => project(bbox, lon, lat));
          const cx = pts.reduce((a, p) => a + p[0], 0) / pts.length;
          const cy = pts.reduce((a, p) => a + p[1], 0) / pts.length;
          const on = highlight === z.id;
          return (
            <g key={z.id}>
              <polygon
                points={pts.map((p) => p.join(',')).join(' ')}
                fill={on ? 'rgba(255,255,255,0.16)' : 'rgba(61,214,140,0.12)'}
                stroke={on ? '#FFFFFF' : '#3DD68C'}
                strokeWidth={on ? 3 : 2}
              />
              <text className="zone-label" x={cx} y={cy} textAnchor="middle" dominantBaseline="middle">
                {z.id}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}
